import React, { createContext, useMemo } from 'react'
import { Font, GLTF, GLTFLoader, FontLoader } from 'three-stdlib'
import mapIcons from './map/icons'
import { useAsync } from 'react-use'

export const fonts = {
  threeFont: {
    defaultBoldRegular: '/montserrat-bold-regular.font.json',
  },
}

export const icons = {
  threeGltf: Object.keys(mapIcons).reduce((acc, key) => {
    acc[key as keyof typeof mapIcons] = mapIcons[key as keyof typeof mapIcons].gltf.url
    return acc
  }, {} as Record<keyof typeof mapIcons, string>),
}

const AssetsContext = createContext<{
  fonts: {
    threeFont: Record<keyof typeof fonts.threeFont, Font | null>
  }
  icons: {
    threeGltf: Record<keyof typeof icons.threeGltf, GLTF | null>
  }
  isLoaded: boolean
  error: Error | null
}>({
  fonts: {
    threeFont: Object.keys(fonts.threeFont).reduce((acc, key) => {
      acc[key as keyof typeof fonts.threeFont] = null
      return acc
    }, {} as Record<keyof typeof fonts.threeFont, Font | null>),
  },
  icons: {
    threeGltf: Object.keys(icons.threeGltf).reduce((acc, key) => {
      acc[key as keyof typeof icons.threeGltf] = null
      return acc
    }, {} as Record<keyof typeof icons.threeGltf, GLTF | null>),
  },
  isLoaded: false,
  error: null,
})
export default AssetsContext

export function AssetsProvider({ children }: { children: React.ReactNode }) {
  const threeFont = useAsync(async () => {
    const fontLoader = new FontLoader()
    const loadedFonts = await Promise.all(
      Object.values(fonts.threeFont).map(font => fontLoader.loadAsync(font)),
    )
    return Object.keys(fonts.threeFont).reduce((acc, key, index) => {
      acc[key as keyof typeof fonts.threeFont] = loadedFonts[index]
      return acc
    }, {} as Record<keyof typeof fonts.threeFont, Font>)
  })
  const threeGltf = useAsync(async () => {
    const gltfLoader = new GLTFLoader()
    const loadedGltfs = await Promise.all(
      Object.values(icons.threeGltf).map(gltf => gltfLoader.loadAsync(gltf)),
    )
    return Object.keys(icons.threeGltf).reduce((acc, key, index) => {
      acc[key as keyof typeof icons.threeGltf] = loadedGltfs[index]
      return acc
    }, {} as Record<keyof typeof icons.threeGltf, GLTF>)
  })

  const value = useMemo(
    () => ({
      fonts: {
        threeFont:
          threeFont.value ||
          Object.keys(fonts.threeFont).reduce((acc, key) => {
            acc[key as keyof typeof fonts.threeFont] = null
            return acc
          }, {} as Record<keyof typeof fonts.threeFont, Font | null>),
      },
      icons: {
        threeGltf:
          threeGltf.value ||
          Object.keys(icons.threeGltf).reduce((acc, key) => {
            acc[key as keyof typeof icons.threeGltf] = null
            return acc
          }, {} as Record<keyof typeof icons.threeGltf, GLTF | null>),
      },
      isLoaded: Boolean(threeFont.value && threeGltf.value),
      error: threeFont.error || threeGltf.error || null,
    }),
    [threeFont.value, threeFont.error, threeGltf.value, threeGltf.error],
  )

  return <AssetsContext.Provider value={value}>{children}</AssetsContext.Provider>
}
